import { forwardRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader, Select, type SelectItemProps, type SelectProps, Stack, Text } from "@mantine/core";
import { omit } from "lodash";
import { Client } from "src/graphql/generated/graphql";

import { useClients } from "../../hooks/clients";
interface ItemProps extends SelectItemProps {
    client: Client;
    displayRole: boolean;
}

const Item = forwardRef<HTMLDivElement, ItemProps>(({ client, displayRole, ...rest }, ref) => {
    const { t } = useTranslation(["common"]);

    return (
        <Stack {...omit(rest, ["value", "label"])} ref={ref} spacing="0">
            <Text weight={500}>{client?.name}</Text>
            {displayRole && <Text weight={300}>{t(`clientuser_role_${client?.role}`)}</Text>}
        </Stack>
    );
});

export interface SelectClientProps extends Partial<Omit<SelectProps, "onChange">> {
    label?: string;
    placeholder?: string;
    value?: string;
    autoselect?: boolean;
    displayRole?: boolean;
    autoselectPrecedence?(client: Client): boolean;
    onChange?(clientId: string, client: Client);
}

export const SelectClient = ({
    label,
    placeholder,
    value,
    autoselect,
    displayRole,
    autoselectPrecedence,
    onChange,
    ...rest
}: SelectClientProps) => {
    const { t } = useTranslation(["common"]);

    const { clients, isLoadingClients } = useClients();

    useEffect(() => {
        if (autoselect && !value && clients?.length) {
            if (typeof autoselectPrecedence === "function") {
                let client = clients.find(autoselectPrecedence);

                if (!client) {
                    client = clients[0];
                }

                onChange?.(client?.id, client);
                return;
            }

            onChange?.(clients?.[0]?.id, clients?.[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, autoselect, autoselectPrecedence, onChange, JSON.stringify(clients)]);

    const handleChange = useCallback(
        (clientId: string) => {
            const client = clients.find((client) => client?.id === clientId);
            onChange?.(client?.id, client);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onChange, JSON.stringify(clients)]
    );

    return (
        <Select
            {...rest}
            label={label || label === null ? label : t("client")}
            placeholder={placeholder || t("client_select")}
            nothingFound={t("nothing_found")}
            icon={isLoadingClients && <Loader size="sm" />}
            itemComponent={Item}
            data={clients.map((client) => ({
                value: client?.id,
                label: `${client?.name} / ${t(`clientuser_role_${client?.role}`)}`,
                client,
                displayRole,
            }))}
            value={value}
            onChange={handleChange}
        />
    );
};
